/** Phase 3B-2a — Decision Quality Score section. */
import { useQuery } from "@tanstack/react-query";
import { Info, Download, TrendingUp, TrendingDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { downloadCsv } from "@/lib/csv";

const TIER_TONE = {
  insufficient_data:            "text-muted-foreground border-border/50",
  needs_review:                 "pnl-down border-current",
  neutral_impact:               "text-signal-yellow border-current",
  positive_contribution:        "pnl-up border-current",
  strong_positive_contribution: "text-signal-green border-current",
};
const TIER_LABEL = {
  insufficient_data:            "Insufficient Data",
  needs_review:                 "Needs Review",
  neutral_impact:               "Neutral Impact",
  positive_contribution:        "Positive Contribution",
  strong_positive_contribution: "Strong Positive Contribution",
};

function fmtPct(v, d = 1) { return v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(d)}%`; }
function toneOf(v) { return v == null || v === 0 ? "" : v > 0 ? "pnl-up" : "pnl-down"; }

function CompareRow({ a, b, aLabel, bLabel, testId }) {
  const aWinsPct = a.avg_return_pct > b.avg_return_pct;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/40" data-testid={testId}>
      {[{ label: aLabel, s: a, better: aWinsPct }, { label: bLabel, s: b, better: !aWinsPct }].map((side, i) => (
        <div key={i} className={`p-3 bg-card/40 ${side.better && side.s.trade_count >= 10 ? "ring-1 ring-signal-green/40" : ""}`}>
          <div className="flex items-center gap-2">
            <div className="mono text-xs">{side.label}</div>
            <div className="ml-auto text-[10px] mono uppercase text-muted-foreground">n={side.s.trade_count}</div>
            {side.s.insufficient_data && <span className="chip !text-[9px] border-border/50 text-muted-foreground">insufficient</span>}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
            <div><div className="label-cap">Win Rate</div><div className="mono">{side.s.win_rate_pct}%</div></div>
            <div><div className="label-cap">Avg Return</div><div className={`mono ${toneOf(side.s.avg_return_pct)}`}>{fmtPct(side.s.avg_return_pct)}</div></div>
            <div><div className="label-cap">PF</div><div className="mono">{side.s.profit_factor.toFixed(2)}</div></div>
            <div><div className="label-cap">Expect $</div><div className={`mono ${toneOf(side.s.expectancy)}`}>{side.s.expectancy > 0 ? "+" : ""}{side.s.expectancy}</div></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DecisionQualitySection({ filterParams }) {
  const { data, isLoading } = useQuery({
    queryKey: qk(["performance", "decision-quality"], { filters: filterParams }),
    queryFn: async () => (await api.get("/performance/decision-quality", { params: Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v != null)) })).data,
    staleTime: STALE.SHORT, gcTime: GC.SHORT,
  });
  if (isLoading || !data) return <div className="grid-cell text-xs text-muted-foreground" data-testid="dq-loading">Loading decision quality…</div>;

  const c = data.comparisons;
  const tier = TIER_LABEL[data.confidence_tier] || data.confidence_tier;
  const tone = TIER_TONE[data.confidence_tier] || "";

  const exportCsv = () => {
    const rows = [];
    Object.entries(c).forEach(([cmp, groups]) => {
      Object.entries(groups).forEach(([g, s]) => {
        rows.push({ comparison: cmp, group: g, ...s });
      });
    });
    downloadCsv("decision-quality.csv", rows, { context: { score: data.score, tier: data.confidence_tier } });
  };

  return (
    <section className="space-y-3" data-testid="perf-dq-section" aria-labelledby="perf-dq-title">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 id="perf-dq-title" className="label-cap">Decision Quality</h2>
        <span className={`chip !text-[10px] uppercase ${tone}`} data-testid={`dq-tier-${data.confidence_tier}`}>{tier}</span>
        {data.score != null && (
          <span className="mono text-2xl ml-2" data-testid="dq-score">{data.score}</span>
        )}
        <span className="text-[10px] mono uppercase text-muted-foreground">n={data.sample_size} · min per group {data.min_group_size}</span>
        <Popover>
          <PopoverTrigger className="text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" aria-label="What is Decision Quality">
            <Info size={12}/>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-xs">
            Decision Quality Score measures whether your interventions helped or hurt returns compared to following the AI plan exactly. 50 = neutral · &gt;65 = strong positive · &lt;45 = needs review. Sample-size gated at {data.min_group_size} trades per comparison group.
          </PopoverContent>
        </Popover>
        <button onClick={exportCsv} className="chip !text-[10px] border-current text-muted-foreground hover:text-foreground ml-auto focus:outline-none focus:ring-1 focus:ring-current" data-testid="dq-export">
          <Download size={10} className="inline mr-1"/>CSV
        </button>
      </div>

      {data.contributors?.length > 0 && (
        <div className="grid-cell !p-3">
          <div className="label-cap mb-2">What the score is telling you</div>
          <ul className="text-xs space-y-1">
            {data.contributors.map((c, i) => (
              <li key={i} className="flex items-center gap-2" data-testid={`dq-contributor-${i}`}>
                {c.value >= 0 ? <TrendingUp size={12} className="pnl-up"/> : <TrendingDown size={12} className="pnl-down"/>}
                <span>{c.factor}</span>
                <span className={`ml-auto mono ${toneOf(c.value)}`}>{c.value > 0 ? "+" : ""}{c.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <div className="label-cap">AI-followed vs Overridden</div>
        <CompareRow
          testId="dq-followed-vs-override"
          a={c.followed_vs_overridden.followed_exactly}
          b={c.followed_vs_overridden.any_override}
          aLabel="AI followed exactly"
          bLabel="Overridden"
        />
      </div>

      <div className="space-y-2">
        <div className="label-cap">Risk Critic accepted vs ignored</div>
        <CompareRow
          testId="dq-critic-accepted-vs-ignored"
          a={c.critic_accepted_vs_ignored.critic_accepted}
          b={c.critic_accepted_vs_ignored.critic_ignored}
          aLabel="Critic accepted"
          bLabel="Critic ignored"
        />
      </div>

      <div className="space-y-2">
        <div className="label-cap">Override by field</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border/40">
          {["entry", "stop", "target", "qty"].map((f) => {
            const s = c.override_by_field[f];
            return (
              <div key={f} className="p-3 bg-card/40" data-testid={`dq-override-${f}`}>
                <div className="mono text-xs uppercase">{f}</div>
                <div className="text-[10px] mono text-muted-foreground">n={s.trade_count}{s.insufficient_data ? " · insufficient" : ""}</div>
                <div className={`mono text-sm mt-1 ${toneOf(s.avg_return_pct)}`}>{fmtPct(s.avg_return_pct)}</div>
                <div className="text-[10px] mono text-muted-foreground">PF {s.profit_factor.toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
